import { useEffect, useState } from 'react';
import { useDdPhases, useDdRequests, useReleaseDdPhase } from '@/api/hooks';
import { ApiError, Button, readableCategoryName } from '@dataroom/shared';
import type { DdPhase } from '@dataroom/shared';
import { Modal } from './Modal';

/** Remembers, per room and per browser, that "Not now" was chosen — so the prompt
 * does not come back on every page load. The Checklist tab's phase table keeps a
 * Release now. */
const dismissKey = (roomId: string) => `closingvault:release-prompt-dismissed:${roomId}`;

export function releasePromptDismissed(roomId: string): boolean {
  try {
    return window.localStorage.getItem(dismissKey(roomId)) === '1';
  } catch {
    return false;
  }
}

function rememberDismissed(roomId: string): void {
  try {
    window.localStorage.setItem(dismissKey(roomId), '1');
  } catch {
    // Private mode or blocked storage: the prompt may return next visit; nothing breaks.
  }
}

/** Phase 1 exists and the seller cannot see it, and nobody has released it. */
export function phaseOneNeedsRelease(phases: readonly DdPhase[] | undefined): boolean {
  const first = phases?.[0];
  return Boolean(first && !first.releasedAt && !first.effectivelyReleased);
}

/** "Phase 2: Legal" — the phase's place in the checklist and its name. A name
 * that already says "Phase 2" is used as it is. */
export function phaseLabel(index: number, name: string): string {
  return /^\s*phase\s*\d+/i.test(name) ? name.trim() : `Phase ${index + 1}: ${name}`;
}

/** A one-line "what this phase covers" from its items: how many, and the top-level
 * categories in the order the checklist lists them. */
function coverLine(total: number, categories: string[]): string {
  if (total === 0) return 'It has no requested items yet.';
  const noun = `${total} requested ${total === 1 ? 'item' : 'items'}`;
  if (categories.length === 0) return `Covers ${noun}.`;
  const shown = categories.slice(0, 4);
  const more = categories.length - shown.length;
  return `Covers ${noun}: ${shown.join(', ')}${more > 0 ? ` and ${more} more` : ''}.`;
}

/**
 * "Release Phase 1 to the seller now?" (owner ask 2026-10-22). Phases are never
 * released by default: once a checklist exists the intermediary is asked about
 * Phase 1, and a yes releases it today. Every phase's start and due dates are then
 * set in the Checklist tab's phase plan, typed in place — `onReleased` hands off
 * to it (owner decision 2026-10-30: the table replaces the one-question-at-a-time
 * date interview).
 *
 * `quietIfNothing`: close at once, without a word, when there is nothing to ask
 * (no checklist, or Phase 1 already released) — for the step after set-up.
 */
export function PhaseReleaseFlow({
  roomId,
  onClose,
  onReleased,
  quietIfNothing = false,
}: {
  roomId: string;
  /** The flow ended: put off with "Not now" / closing the dialog, or released. */
  onClose: () => void;
  /** Phase 1 was released: show the phase plan so its dates can be set. Called
   * before `onClose`. */
  onReleased?: () => void;
  quietIfNothing?: boolean;
}) {
  const phasesQuery = useDdPhases(roomId);
  const phases = phasesQuery.data;
  const first = phases?.[0];
  const needs = phaseOneNeedsRelease(phases);
  // Latched once asked, so the question stays up through the answer even though a
  // Yes makes "needs release" false underneath it.
  const [asked, setAsked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const release = useReleaseDdPhase(roomId);

  // What the first phase holds, for the one-line description.
  const items = useDdRequests(roomId, { phaseId: first?.id, limit: 100, offset: 0 });
  const categories: string[] = [];
  for (const r of items.data?.requests ?? []) {
    const top = r.categoryPath?.[0];
    if (top) {
      const name = readableCategoryName(top);
      if (!categories.includes(name)) categories.push(name);
    }
  }

  const settled = phasesQuery.isSuccess && !phasesQuery.isFetching;
  const idle = quietIfNothing && settled && !asked && !needs;
  useEffect(() => {
    if (needs) setAsked(true);
  }, [needs]);
  useEffect(() => {
    if (idle) onClose();
  }, [idle, onClose]);

  if (idle || !phases || !first) return null;
  if (!needs && !asked) {
    // Opened by hand with nothing to release (released elsewhere meanwhile).
    return (
      <Modal
        title="Phase 1 is already released"
        onClose={onClose}
        footer={<Button onClick={onClose}>Close</Button>}
      >
        <p className="text-sm">The seller can already see {phaseLabel(0, first.name)}.</p>
      </Modal>
    );
  }

  const dismiss = () => {
    rememberDismissed(roomId);
    onClose();
  };

  const yes = async () => {
    setError(null);
    try {
      await release.mutateAsync(first.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not release the phase. Try again.');
      return;
    }
    onReleased?.();
    onClose();
  };

  return (
    <Modal
      title={`Release ${phaseLabel(0, first.name)} to the seller now?`}
      description={items.isSuccess ? coverLine(items.data.total, categories) : undefined}
      onClose={dismiss}
      footer={
        <>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : (
            <span className="text-xs text-muted-foreground">
              Releasing makes this phase visible on the seller&apos;s checklist.
            </span>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={dismiss} disabled={release.isPending}>
              Not now
            </Button>
            <Button size="sm" onClick={() => void yes()} disabled={release.isPending}>
              {release.isPending ? 'Releasing…' : 'Yes, release now'}
            </Button>
          </div>
        </>
      }
    >
      <p className="text-sm">
        Phases are not released on their own. If you release this one now, the seller can start on
        it today. Then set each phase&apos;s start and due dates in the phase table.
      </p>
    </Modal>
  );
}

/**
 * Opening a room whose Phase 1 has never been released asks about it once (the
 * Checklist tab's phase table keeps a Release now for later). "Not now" is
 * remembered for this room in this browser, so the question does not come back on
 * every page load. Latched: once it has opened it stays up through the answer, even
 * though a Yes makes "needs release" false underneath it.
 */
export function ReleasePromptOnOpen({
  roomId,
  onReleased,
}: {
  roomId: string;
  onReleased?: () => void;
}) {
  const phases = useDdPhases(roomId);
  const [asking, setAsking] = useState(false);
  const [closed, setClosed] = useState(false);
  const wanted = phases.isSuccess && phaseOneNeedsRelease(phases.data);
  useEffect(() => {
    if (wanted && !releasePromptDismissed(roomId)) setAsking(true);
  }, [wanted, roomId]);
  if (!asking || closed) return null;
  return (
    <PhaseReleaseFlow roomId={roomId} onReleased={onReleased} onClose={() => setClosed(true)} />
  );
}
